import { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { Search } from "lucide-react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { toast, ToastContainer } from "react-toastify";
import "../../Styles/yellowcalfcss/ExpenseType.css";
import ConfirmModal from "../../components/ConfirmModal";
import { simplePostCall } from "../../api/ApiServices";
import ApiConfig from "../../api/ApiConfig";
import ScaleLoader from "react-spinners/ScaleLoader";

export function ExpenseType() {
  /* ---------------- STATE ---------------- */
  const customer_currency = (localStorage.getItem("customer_currency") || "").replace(/"/g, "");
  const [recordsList, setRecordsList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [nameSearch, setNameSearch] = useState("");
  const [priceSearch, setPriceSearch] = useState("");

  const [displayModal, setDisplayModal] = useState(false);
  const [currentEditingRecord, setCurrentEditingRecord] = useState(null);

  const [formName, setFormName] = useState("");
  const [formPrice, setFormPrice] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  /* ---------------- FETCH LIST ---------------- */
  const fetchExpenseTypes = async () => {
    setLoading(true);

    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      name_search: nameSearch,
      price_search: priceSearch,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.expenseTypeList,
        JSON.stringify(requestBody)
      );

      if (res?.result) {
        setRecordsList(res.data || []);
      } else {
        setRecordsList([]);
        toast.error(res?.message || "Failed to load data");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenseTypes();
  }, []);

  /* ---------------- VALIDATION ---------------- */
  const validateForm = () => {
    const errors = {};
    if (!formName.trim()) errors.name = "Name is required";
    if (!formPrice || Number(formPrice) <= 0)
      errors.price = "Valid price required";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /* ---------------- CREATE / UPDATE ---------------- */
  const handleSaveRecord = async () => {
    if (!validateForm()) return;

    setSaving(true);

    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      name: formName,
      price: Number(formPrice),
      ...(currentEditingRecord && { id: currentEditingRecord.expense_type_id }),
    };

    const api = currentEditingRecord
      ? ApiConfig.expenseTypeUpdate
      : ApiConfig.expenseTypeCreate;

    try {
      const res = await simplePostCall(api, JSON.stringify(requestBody));
      if (res?.result) {
        toast.success(res.message);
        setDisplayModal(false);
        fetchExpenseTypes();
      } else {
        toast.error(res?.message || "Operation failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setSaving(false);
    }
  };

  /* ---------------- DELETE ---------------- */
  const handleDeleteIncome = async () => {
    setIsDeleting(true);

    const requestBody = {
      customer_id: Number(localStorage.getItem("customer_id")),
      api_key: localStorage.getItem("api_key"),
      user_id: Number(localStorage.getItem("user_id")),
      language: "en",
      id: deleteId,
    };

    try {
      const res = await simplePostCall(
        ApiConfig.expenseTypeDelete,
        JSON.stringify(requestBody)
      );

      if (res?.result) {
        toast.success(res.message);
        fetchExpenseTypes();
      } else {
        toast.error(res?.message || "Delete failed");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  useEffect(() => {
  const timer = setTimeout(() => {
    fetchExpenseTypes();
  }, 500); // ⏳ 500ms debounce

  return () => clearTimeout(timer);
}, [nameSearch, priceSearch]);

  /* ---------------- UI ---------------- */
  return (
    <div className="income-type-container">
       <ToastContainer position="top-right" autoClose={3000} />
      {/* HEADER */}
      <div className="top-header-section" style={{ height: "58px" }}>
        <h1 className="page-title-text">Expense Type</h1>
        <div className="action-buttons-group">
          <Button
            className="create-new-button"
            onClick={() => {
              setCurrentEditingRecord(null);
              setFormName("");
              setFormPrice("");
              setFormErrors({});
              setDisplayModal(true);
            }}
          >
            Create Expense Type
          </Button>
        </div>
      </div>

      {/* FILTERS (unchanged layout) */}

      <div className="search-filter-panel-- d-flex gap-4 mb-3" style={{ marginTop: "10px" }}>
        <Form.Control
          placeholder="Search Name"
          value={nameSearch}
          onChange={(e) => setNameSearch(e.target.value)}
          style={{width : '150px'}}
          // onBlur={fetchExpenseTypes}
        />
        <Form.Control
          placeholder="Search Price"
          value={priceSearch}
          onChange={(e) => setPriceSearch(e.target.value)}
          style={{width : '250px'}}
          // onBlur={fetchExpenseTypes}
        />
      </div>

      {/* ORIGINAL TABLE HEADER */}
     <div
  className="table-headerse-row d-flex align-items-center px-3"
  style={{
    minHeight: "55px",
    background: "#ffffff",
    borderBottom: "2px solid #ddd",
    fontWeight: "600",
  }}
>
  <div style={{ width: "10%", textAlign: "center" }}>
    Sr.No
  </div>

  <div style={{ width: "40%", textAlign: "center" }}>
    Name
  </div>

  <div style={{ width: "25%", textAlign: "center" }}>
    Price
  </div>

  <div style={{ width: "25%", textAlign: "center" }}>
    Actions
  </div>
</div>

      {/* ORIGINAL TABLE BODY */}
      <div className="records-display-area">
  {loading ? (
    <div className="text-center py-2">
      <ScaleLoader color="#F4BB44" />
    </div>
  ) : recordsList.length === 0 ? (
    <div className="text-center py-4">No data found</div>
  ) : (
    recordsList.map((record, index) => (
      <div
        key={record.expense_type_id}
        className="single-record-line d-flex align-items-center justify-content-between px-3"
        style={{
          minHeight: "55px",
          borderBottom: "1px solid #eee",
        }}
      >
        {/* Serial No */}
        <div style={{ width: "10%", textAlign: "center" }}>
          {index + 1}
        </div>

        {/* Name */}
        <div style={{ width: "40%", textAlign: "center" }}>
          {record.expense_type_name}
        </div>

        {/* Price */}
        <div style={{ width: "25%", textAlign: "center" }}>
          {customer_currency}{record.expense_type_price}
        </div>

        {/* Buttons */}
        <div
          style={{ width: "25%" }}
          className="d-flex justify-content-center gap-2"
        >
          <button
            className="modify-icon-control"
            onClick={() => {
              setCurrentEditingRecord(record);
              setFormName(record.expense_type_name);
              setFormPrice(record.expense_type_price);
              setFormErrors({});
              setDisplayModal(true);
            }}
          >
            <FaEdit size={16} />
          </button>

          <button
            className="remove-icon-control"
            onClick={() => {
              setDeleteId(record.expense_type_id);
              setShowDeleteModal(true);
            }}
          >
            <FaTrashAlt color="red" />
          </button>
        </div>
      </div>
    ))
  )}
</div>

      {/* CREATE / EDIT MODAL */}
      <Modal show={displayModal} onHide={() => setDisplayModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {currentEditingRecord ? "Edit Expense Type" : "Create Expense Type"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group>
              <Form.Label>Name</Form.Label>
              <Form.Control
                value={formName}
                isInvalid={!!formErrors.name}
                onChange={(e) => setFormName(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mt-3">
              <Form.Label>Price</Form.Label>
              <Form.Control
                type="number"
                value={formPrice}
                isInvalid={!!formErrors.price}
                onChange={(e) => setFormPrice(e.target.value)}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.price}
              </Form.Control.Feedback>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDisplayModal(false)}>
            Cancel
          </Button>
          <Button onClick={handleSaveRecord} disabled={saving}>
            {saving ? <Spinner size="sm" /> : "Save"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* DELETE CONFIRM */}
      <ConfirmModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title="Delete Income"
        message="Are you sure you want to delete this income record?"
        confirmText="Delete"
        confirmVariant="danger"
        onConfirm={handleDeleteIncome}
        isLoading={isDeleting}
      />
    </div>
  );
}
